/**
 * Client Session Creator - REBUILT
 *
 * Simplified practitioner session generation using new architecture.
 * Features:
 * - Single unified hook (no complex parameter passing)
 * - Automatic cache collision prevention
 * - Server-side audio mixing with 5-second fade-out
 * - Type-safe throughout
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Copy, Share2, User, Clock, Music, ArrowLeft, Coins, Mail, CheckCircle, FileText, Wand2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { backendClient } from '@/lib/backendClient';

// NEW: Simplified imports
import usePractitionerSessionGeneration from '@/hooks/usePractitionerSessionGeneration';
import { usePractitionerClients } from '@/hooks/usePractitionerClients';
import { usePractitionerSessions } from '@/hooks/usePractitionerSessions';
import { useGroupSessions } from '@/hooks/useGroupSessions';
import CategorySpecificPersonalizationQuestionnaire from '@/components/CategorySpecificPersonalizationQuestionnaire';
import GroupSessionSelector from '@/components/group/GroupSessionSelector';
import EnhancedAudioPlayer from '@/components/EnhancedAudioPlayer';
import { CreditCalculator } from './CreditCalculator';
import { getBackgroundMusicUrl } from '@/hooks/useBackgroundMusic';

// Types
import type { PractitionerClient } from '@/types/practitioner';
import type { QuestionnaireData } from '@/types/category';
import { categories } from '@/config/categories';
import { GroupSessionScope } from '@/types/groupSession';
import { usePricingRates } from '@/hooks/usePricingRates';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { creditsToMinutes } from '@/utils/creditConversionUtils';
import {
  CUSTOM_TEXT_MAX_LENGTH,
  CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH,
  CUSTOM_TEXT_MIN_LENGTH,
  CUSTOM_TEXT_INSTRUCTIONS_MIN_LENGTH,
  type CustomTextMode,
  validateCustomText,
  validateCustomTextInstructions,
  estimateWordCount,
  estimateDuration,
  VALIDATION_ERRORS,
} from '@/constants/customText';

// Filter categories
const EXCLUDED_CATEGORIES = ['news-meditation'];
const CATEGORIES = categories
  .filter(category => !EXCLUDED_CATEGORIES.includes(category.id))
  .map(category => ({
    id: category.id,
    name: category.name,
    description: category.description
  }));

interface ClientSessionCreatorProps {
  onSessionCreated?: (sessionId: string, shareUrl: string) => void;
}

export const ClientSessionCreator = ({ onSessionCreated }: ClientSessionCreatorProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { audioRate } = usePricingRates();
  const { credits, totalAvailableCredits } = usePractitionerCredits();

  // NEW: Single unified hook
  const {
    generateSession,
    isGenerating,
    progress,
    error: generationError,
    result,
    hasResult,
    reset: resetGeneration
  } = usePractitionerSessionGeneration();

  const {
    clients,
    saveClientQuestionnairePreferences,
    getClientQuestionnairePreferences
  } = usePractitionerClients();

  const {
    saveGroupQuestionnairePreferences,
    getGroupQuestionnairePreferences
  } = useGroupSessions();

  const { createSharedLink } = usePractitionerSessions();

  // Practitioner profile
  const [practitionerProfile, setPractitionerProfile] = useState<{
    full_name?: string;
    first_name?: string;
    last_name?: string
  } | null>(null);

  // Workflow state
  const [currentStep, setCurrentStep] = useState<'selection' | 'questionnaire' | 'result'>('selection');

  // Session configuration
  const [sessionScope, setSessionScope] = useState<GroupSessionScope>({ sessionScope: 'individual' });
  const [selectedClient, setSelectedClient] = useState<PractitionerClient | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [sessionTitle, setSessionTitle] = useState<string>('');
  const [sessionNotes, setSessionNotes] = useState<string>('');
  const [customPrompt, setCustomPrompt] = useState<string>('');

  // Share link state
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Questionnaire data
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData | null>(null);
  const [savedPreferences, setSavedPreferences] = useState<QuestionnaireData | null>(null);

  // Custom text mode state (CustomTextMode imported from constants)
  const [useCustomText, setUseCustomText] = useState<boolean>(false);
  const [customTextMode, setCustomTextMode] = useState<CustomTextMode>('verbatim');
  const [customText, setCustomText] = useState<string>('');
  const [customTextInstructions, setCustomTextInstructions] = useState<string>('');

  // Load practitioner profile
  // Dependencies include user name fields to refresh when profile is updated
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) return;

      const { data, error } = await backendClient
        .from('profiles')
        .select('full_name, first_name, last_name')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setPractitionerProfile(data);
      }
    };

    loadProfile();
  }, [user?.id, user?.first_name, user?.last_name, user?.full_name]);

  // Load saved preferences when client OR group is selected
  useEffect(() => {
    const loadPreferences = async () => {
      if (selectedClient && selectedCategory) {
        // Load preferences for individual client
        const savedPrefs = await getClientQuestionnairePreferences(
          selectedClient.id,
          selectedCategory
        );

        if (savedPrefs) {
          console.log('📥 Loaded saved preferences for client:', savedPrefs);
          setSavedPreferences(savedPrefs);
        } else {
          // Clear saved preferences if none found
          setSavedPreferences(null);
        }
      } else if (sessionScope.groupSession && selectedCategory) {
        // Load preferences for group session (using group-specific function)
        const savedPrefs = getGroupQuestionnairePreferences(
          sessionScope.groupSession.id,
          selectedCategory
        );

        if (savedPrefs) {
          console.log('📥 Loaded saved preferences for group:', savedPrefs);
          setSavedPreferences(savedPrefs);
        } else {
          // Clear saved preferences if none found
          setSavedPreferences(null);
        }
      } else {
        // Clear saved preferences when client/category changes
        setSavedPreferences(null);
      }
    };

    loadPreferences();
  }, [selectedClient, selectedCategory, sessionScope.groupSession, getClientQuestionnairePreferences, getGroupQuestionnairePreferences]);

  // Debug effect to track state changes
  useEffect(() => {
    console.log('🔍 State update:', {
      currentStep,
      isGenerating,
      hasResult,
      hasResultObject: !!result,
      resultSessionId: result?.sessionId
    });
  }, [currentStep, isGenerating, hasResult, result]);

  /**
   * Handle questionnaire submission - SIMPLIFIED!
   */
  const handleQuestionnaireSubmit = async (data: QuestionnaireData) => {
    if (!selectedClient && !sessionScope.groupSession) {
      toast({
        title: "Error",
        description: "Please select a client or group first",
        variant: "destructive",
      });
      return;
    }

    if (!selectedCategory) {
      toast({
        title: "Error",
        description: "Please select a category",
        variant: "destructive",
      });
      return;
    }

    // VALIDATION: Check group session has participants
    if (sessionScope.sessionScope === 'group' && sessionScope.groupSession) {
      const participants = sessionScope.groupSession.participants;

      if (!participants || !Array.isArray(participants) || participants.length === 0) {
        toast({
          title: "No Participants",
          description: "Group session must have at least one participant. Please add participants to your group first.",
          variant: "destructive",
        });
        return;
      }

      // Check for at least one valid participant with a name
      const validParticipants = participants.filter(
        (p: any) => p && (p.firstName?.trim() || p.preferredName?.trim())
      );

      if (validParticipants.length === 0) {
        toast({
          title: "Invalid Participants",
          description: "At least one participant must have a valid name. Please check your group configuration.",
          variant: "destructive",
        });
        return;
      }

      console.log(`✓ Group session validation passed: ${validParticipants.length} valid participants`);
    }

    // VALIDATION: Check custom text requirements using shared constants
    if (useCustomText) {
      const textValidation = validateCustomText(customText, customTextMode);
      if (!textValidation.isValid) {
        toast({
          title: "Custom Text Error",
          description: textValidation.error,
          variant: "destructive",
        });
        return;
      }

      const instructionsValidation = validateCustomTextInstructions(customTextInstructions, customTextMode);
      if (!instructionsValidation.isValid) {
        toast({
          title: "Instructions Error",
          description: instructionsValidation.error,
          variant: "destructive",
        });
        return;
      }
    }

    setQuestionnaireData(data);

    try {
      // Save preferences for future sessions (category-specific)
      if (selectedClient && selectedCategory) {
        // Save for individual client
        await saveClientQuestionnairePreferences(
          selectedClient.id,
          selectedCategory,
          data
        );
      } else if (sessionScope.groupSession && selectedCategory) {
        // Save for group session (using group-specific function)
        await saveGroupQuestionnairePreferences(
          sessionScope.groupSession.id,
          selectedCategory,
          data
        );
      }

      // Extract voice and background music from questionnaire data
      // IMPORTANT: specific_voice_id is the actual voice ID (e.g., "female_default", "male_coach")
      // voiceType is the voice STYLE description (e.g., "calm", "Confident empowering voice") - NOT a valid voice ID
      const voiceGender = data.voiceGender || 'female';
      const selectedVoice = data.specific_voice_id || `${voiceGender}_default`;
      const backgroundMusicFile = data.backgroundMusicSelection || 'universal_background.mp3';
      const enableBackgroundMusic = data.backgroundSounds !== false; // Default to true

      // Generate title if not provided
      const finalTitle = sessionTitle || (
        selectedClient
          ? `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Session for ${selectedClient.client_name}`
          : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Group Session`
      );

      console.log('🎯 Generating session with NEW simplified architecture...');
      console.log('Voice from questionnaire:', { selectedVoice, voiceGender, backgroundMusicFile, enableBackgroundMusic });

      // NEW: Single clean API call
      await generateSession({
        // Basic info
        title: finalTitle,
        clientId: selectedClient?.id,
        groupSessionId: sessionScope.groupSession?.id,
        categoryId: selectedCategory,

        // Questionnaire data (mapper handles all transformation)
        questionnaireData: data,

        // Optional configuration
        sessionNotes,
        customPrompt: customPrompt || undefined,

        // Custom text mode fields
        custom_text_mode: useCustomText ? customTextMode : undefined,
        custom_text: useCustomText ? customText.trim() : undefined,
        custom_text_instructions: useCustomText && customTextMode === 'blend_custom' ? customTextInstructions.trim() : undefined,

        // Voice & audio (from questionnaire)
        voiceGender,
        specific_voice_id: selectedVoice,
        backgroundMusicFile: enableBackgroundMusic ? backgroundMusicFile : undefined,
        saveMixedVersion: true, // Always create mixed audio

        // Client info (ONLY for individual client sessions, NOT for group sessions)
        // FIX: Don't send client names for group sessions (no individual client)
        // FIX: Don't send practitioner name (backend derives it from database)
        clientFirstName: selectedClient ? selectedClient.first_name : undefined,
        clientLastName: selectedClient ? selectedClient.last_name : undefined,
        practitionerName: undefined, // Let backend derive from database
      });

      // Success! Move to result step
      console.log('✅ Generation complete! Setting step to result. Has result:', hasResult, 'Result:', result);
      setCurrentStep('result');

      toast({
        title: "Session Generated!",
        description: "Your audio session is ready. You can now create a share link.",
      });

    } catch (error) {
      console.error('❌ Session generation failed:', error);

      const errorMessage = error instanceof Error
        ? error.message
        : "Failed to generate the session. Please try again.";

      toast({
        title: "Generation Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  /**
   * Create share link for the generated session
   */
  const handleCreateShareLink = async () => {
    if (!result?.sessionId) return;

    setIsCreatingLink(true);

    try {
      const shareUrlResult = await createSharedLink(result.sessionId);

      if (!shareUrlResult) {
        throw new Error('Failed to create shared link');
      }

      setShareUrl(shareUrlResult);

      toast({
        title: "Share Link Created!",
        description: "You can now copy and share this link with your client.",
      });

      // Notify parent if callback provided
      if (onSessionCreated) {
        onSessionCreated(result.sessionId, shareUrlResult);
      }

    } catch (error) {
      console.error('Failed to create share link:', error);

      toast({
        title: "Failed to Create Share Link",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingLink(false);
    }
  };

  /**
   * Copy share link to clipboard
   */
  const handleCopyLink = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);

      toast({
        title: "Link Copied!",
        description: "Share link has been copied to clipboard.",
      });
    } catch (error) {
      toast({
        title: "Failed to Copy",
        description: "Please copy the link manually.",
        variant: "destructive",
      });
    }
  };

  /**
   * Send email with share link
   */
  const handleSendEmail = async () => {
    if (!result?.sessionId || !selectedClient?.email) return;

    setIsSendingEmail(true);

    try {
      // Create share link first if we don't have one
      let linkToSend = shareUrl;
      if (!linkToSend) {
        linkToSend = await createSharedLink(result.sessionId);
        if (!linkToSend) {
          throw new Error('Failed to create shared link');
        }
        setShareUrl(linkToSend);
      }

      // TODO: Implement email sending via backend API
      // For now, just copy to clipboard and show instructions
      await navigator.clipboard.writeText(linkToSend);

      toast({
        title: "Link Copied!",
        description: `Share link copied. Please send it to ${selectedClient.email}`,
      });

    } catch (error) {
      console.error('Failed to send email:', error);

      toast({
        title: "Failed to Send Email",
        description: "Please try again or share the link manually.",
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  /**
   * Reset and start over
   */
  const handleReset = () => {
    resetGeneration();
    setCurrentStep('selection');
    setSelectedClient(null);
    setSelectedCategory('');
    setSessionTitle('');
    setSessionNotes('');
    setCustomPrompt('');
    setQuestionnaireData(null);
    setShareUrl('');
    // Reset custom text state
    setUseCustomText(false);
    setCustomTextMode('verbatim');
    setCustomText('');
    setCustomTextInstructions('');
  };

  /**
   * Go back to previous step
   */
  const handleBack = () => {
    if (currentStep === 'questionnaire') {
      setCurrentStep('selection');
    } else if (currentStep === 'result') {
      setCurrentStep('selection');
      resetGeneration();
    }
  };

  // Calculate estimated cost
  const sessionLength = questionnaireData?.sessionLength || '10 minutes';
  const lengthInMinutes = parseInt(sessionLength) || 10;

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Create Client Session</h1>
          <p className="text-muted-foreground">Generate personalized meditation sessions for your clients</p>
        </div>

        {/* Credits display */}
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-amber-500" />
          <div className="text-right">
            <span className="text-lg font-semibold block">{totalAvailableCredits} credits</span>
            {audioRate > 0 && (
              <span className="text-xs text-muted-foreground">
                ~{creditsToMinutes(totalAvailableCredits, audioRate)} min available
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-center gap-4">
        <div className={`flex items-center gap-2 ${currentStep === 'selection' ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'selection' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
            1
          </div>
          <span className="text-sm font-medium">Selection</span>
        </div>

        <div className="w-12 h-0.5 bg-border"></div>

        <div className={`flex items-center gap-2 ${currentStep === 'questionnaire' ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'questionnaire' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
            2
          </div>
          <span className="text-sm font-medium">Questionnaire</span>
        </div>

        <div className="w-12 h-0.5 bg-border"></div>

        <div className={`flex items-center gap-2 ${currentStep === 'result' ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'result' ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
            3
          </div>
          <span className="text-sm font-medium">Result</span>
        </div>
      </div>

      {/* STEP 1: Selection */}
      {currentStep === 'selection' && (
        <Card>
          <CardHeader>
            <CardTitle>Session Configuration</CardTitle>
            <CardDescription>Select client/group and meditation category</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Session scope selector (individual vs group) */}
            <GroupSessionSelector
              value={sessionScope}
              onChange={setSessionScope}
            />

            {/* Individual client selection */}
            {sessionScope.sessionScope === 'individual' && (
              <div className="space-y-2">
                <Label htmlFor="client">Select Client *</Label>
                <Select
                  value={selectedClient?.id || ''}
                  onValueChange={(clientId) => {
                    const client = clients.find(c => c.id === clientId);
                    setSelectedClient(client || null);
                  }}
                >
                  <SelectTrigger id="client">
                    <SelectValue placeholder="Choose a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Category selection */}
            <div className="space-y-2">
              <Label htmlFor="category">Meditation Category *</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger id="category">
                  <SelectValue placeholder="Choose a category..." />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Session title */}
            <div className="space-y-2">
              <Label htmlFor="title">Session Title (optional)</Label>
              <Input
                id="title"
                value={sessionTitle}
                onChange={(e) => setSessionTitle(e.target.value)}
                placeholder="Leave blank for auto-generated title"
              />
            </div>

            {/* Session notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Session Notes (optional)</Label>
              <Textarea
                id="notes"
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                placeholder="Private notes about this session (e.g., 'Focus on breathing exercises')"
                rows={3}
              />
            </div>

            {/* Custom Text Mode Section */}
            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base flex items-center gap-2">
                    {useCustomText ? <FileText className="h-4 w-4" /> : <Wand2 className="h-4 w-4" />}
                    {useCustomText ? 'Include Custom Text' : 'AI-Generated Script'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {useCustomText
                      ? 'Use your own script or blend it with AI'
                      : 'AI will generate the meditation script'}
                  </p>
                </div>
                <Switch
                  checked={useCustomText}
                  onCheckedChange={setUseCustomText}
                />
              </div>

              {useCustomText && (
                <div className="space-y-4 pt-2">
                  {/* Mode selector */}
                  <div className="space-y-2">
                    <Label htmlFor="custom-text-mode">How should the AI use your text?</Label>
                    <Select
                      value={customTextMode}
                      onValueChange={(value: CustomTextMode) => setCustomTextMode(value)}
                    >
                      <SelectTrigger id="custom-text-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="verbatim">Use Exactly As Written (Skip AI)</SelectItem>
                        <SelectItem value="blend_beginning">AI Starts with My Text</SelectItem>
                        <SelectItem value="blend_end">AI Ends with My Text</SelectItem>
                        <SelectItem value="blend_middle">AI Incorporates at Middle</SelectItem>
                        <SelectItem value="blend_woven">AI Weaves Themes Throughout</SelectItem>
                        <SelectItem value="blend_custom">Custom Instructions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Custom text textarea */}
                  <div className="space-y-2">
                    <Label htmlFor="custom-text">
                      {customTextMode === 'verbatim' ? 'Your Meditation Script' : 'Your Custom Text'}
                    </Label>
                    <Textarea
                      id="custom-text"
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      placeholder={
                        customTextMode === 'verbatim'
                          ? 'Enter your complete meditation script here...'
                          : 'Enter the text or themes you want the AI to incorporate...'
                      }
                      rows={8}
                      maxLength={CUSTOM_TEXT_MAX_LENGTH}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        Words: {estimateWordCount(customText)} |
                        Est. duration: {estimateDuration(customText)} min
                      </span>
                      <span className={customText.length > CUSTOM_TEXT_MAX_LENGTH * 0.9 ? 'text-amber-500' : ''}>
                        {customText.length.toLocaleString()}/{CUSTOM_TEXT_MAX_LENGTH.toLocaleString()} chars
                      </span>
                    </div>
                  </div>

                  {/* Custom instructions (only for blend_custom mode) */}
                  {customTextMode === 'blend_custom' && (
                    <div className="space-y-2">
                      <Label htmlFor="custom-instructions">Instructions for AI</Label>
                      <Textarea
                        id="custom-instructions"
                        value={customTextInstructions}
                        onChange={(e) => setCustomTextInstructions(e.target.value)}
                        placeholder="Tell the AI exactly how to use your text (e.g., 'Use my text as the opening, then transition into a body scan')"
                        rows={3}
                        maxLength={CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH}
                      />
                      <div className="text-xs text-muted-foreground text-right">
                        <span className={customTextInstructions.length > CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH * 0.9 ? 'text-amber-500' : ''}>
                          {customTextInstructions.length.toLocaleString()}/{CUSTOM_TEXT_INSTRUCTIONS_MAX_LENGTH.toLocaleString()} chars
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Tips section */}
                  <div className="bg-muted/50 rounded-md p-3 text-sm">
                    <p className="font-medium mb-1">
                      {customTextMode === 'verbatim' ? 'Tips for Verbatim Mode:' : 'Tips for Blend Modes:'}
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      {customTextMode === 'verbatim' ? (
                        <>
                          <li>Your text will be read exactly as written</li>
                          <li>Include pauses by writing "... (pause) ..."</li>
                          <li>Write in second person ("you feel calm...")</li>
                        </>
                      ) : (
                        <>
                          <li>AI will maintain your style and themes</li>
                          <li>Include key phrases you want preserved</li>
                          <li>Questionnaire answers still influence the session</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Continue button */}
            <Button
              onClick={() => setCurrentStep('questionnaire')}
              disabled={!selectedCategory || (!selectedClient && !sessionScope.groupSession)}
              className="w-full"
              size="lg"
            >
              Continue to Questionnaire
            </Button>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Questionnaire */}
      {currentStep === 'questionnaire' && !isGenerating && (
        <div>
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Selection
          </Button>

          {(() => {
            // Debug logging for group session issue
            const sessionTypeValue = sessionScope.sessionScope === 'individual' ? 'individual' : 'group';
            const isGroupSessionValue = sessionScope.sessionScope === 'group';
            console.log("🔥 ClientSessionCreator passing to questionnaire:", {
              sessionScope: sessionScope.sessionScope,
              sessionTypeValue,
              isGroupSessionValue,
              hasGroupSession: !!sessionScope.groupSession,
              groupName: sessionScope.groupSession?.group_name,
              selectedClient: selectedClient?.client_name
            });

            return (
              <CategorySpecificPersonalizationQuestionnaire
                categoryName={selectedCategory}
                categoryId={selectedCategory}
                onSubmit={handleQuestionnaireSubmit}
                onCancel={handleBack}
                isGenerating={isGenerating}
                sessionType={sessionTypeValue}
                isGroupSession={isGroupSessionValue}
                groupName={sessionScope.groupSession?.group_name}
                isPractitionerMode={true}
                clientName={selectedClient?.client_name}
                clientData={selectedClient}
                initialData={savedPreferences}
              />
            );
          })()}

          {/* Credit calculator */}
          <Card className="mt-4">
            <CardContent className="pt-6">
              <CreditCalculator
                durationMinutes={lengthInMinutes}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Generation Progress - Hide questionnaire during generation */}
      {currentStep === 'questionnaire' && isGenerating && (
        <Card>
          <CardContent className="py-8">
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">Generating Your Session...</h3>
                <p className="text-muted-foreground">
                  This may take 1-2 minutes. Please don't close this page.
                </p>
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="w-full bg-secondary rounded-full h-3">
                  <div
                    className="bg-primary h-3 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                  />
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  {Math.round(progress)}% complete
                </p>
              </div>

              {/* Status messages */}
              <div className="text-center text-sm text-muted-foreground">
                {progress < 25 && "Validating session parameters..."}
                {progress >= 25 && progress < 50 && "Generating personalized content..."}
                {progress >= 50 && progress < 75 && "Creating voice audio..."}
                {progress >= 75 && progress < 100 && "Mixing audio with background music and fade-out..."}
                {progress >= 100 && "Finalizing session..."}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3: Result */}
      {currentStep === 'result' && (
        <div className="space-y-6">
          {!result ? (
            <Card>
              <CardContent className="py-8">
                <div className="text-center">
                  <p className="text-muted-foreground">Loading result...</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <CardTitle>Session Generated Successfully!</CardTitle>
                  </div>
                  <CardDescription>
                    Your personalized meditation session is ready to share
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Audio player - Main focus */}
                  {result.mixedAudioUrl ? (
                    // Mixed audio: Music already baked in - don't pass backgroundAudioUrl
                    <EnhancedAudioPlayer
                      audioUrl={result.mixedAudioUrl}
                      sessionTitle={sessionTitle || "Meditation Session"}
                      sessionId={result.sessionId}
                    />
                  ) : result.audioUrl ? (
                    // Voice-only audio: Pass backgroundAudioUrl for client-side mixing
                    <EnhancedAudioPlayer
                      audioUrl={result.audioUrl}
                      sessionTitle={sessionTitle || "Meditation Session"}
                      sessionId={result.sessionId}
                      backgroundAudioUrl={
                        result.backgroundAudioUrl ||
                        (questionnaireData?.backgroundMusicSelection
                          ? getBackgroundMusicUrl(questionnaireData.backgroundMusicSelection)
                          : undefined)
                      }
                    />
                  ) : null}

              {/* Share link section */}
              {shareUrl ? (
                <div className="space-y-3 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900">
                  <Label>Share Link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={shareUrl}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      size="icon"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>

                  {selectedClient?.email && (
                    <Button
                      onClick={handleSendEmail}
                      disabled={isSendingEmail}
                      variant="outline"
                      className="w-full"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      {isSendingEmail ? 'Sending...' : `Email to ${selectedClient.email}`}
                    </Button>
                  )}
                </div>
              ) : (
                <Button
                  onClick={handleCreateShareLink}
                  disabled={isCreatingLink}
                  size="lg"
                  className="w-full"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  {isCreatingLink ? 'Creating Link...' : 'Create Share Link'}
                </Button>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="flex-1"
                >
                  Create Another Session
                </Button>
              </div>
            </CardContent>
          </Card>
            </>
          )}
        </div>
      )}

      {/* Error display */}
      {generationError && !isGenerating && (
        <Card className="border-destructive">
          <CardContent className="py-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-destructive mb-2">
                  Generation Failed
                </h3>
                <p className="text-sm text-muted-foreground">
                  {generationError}
                </p>
              </div>

              <Button
                onClick={() => {
                  resetGeneration();
                  setCurrentStep('questionnaire');
                }}
                variant="outline"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientSessionCreator;
